import type { StreamingService } from '@/constants/streamingServices';
import { normalizeServiceId } from '@/constants/streamingServices';

const PARTNER_PNG: Record<string, string> = {
  spotify: '/images/partners/spotify.png',
  deezer: '/images/partners/deezer.png',
  apple_music: '/images/partners/apple-music.png',
};

/**
 * Brand logo for a streaming service — PNG for Spotify/Deezer/Apple Music,
 * inline SVG for SoundCloud/YouTube Music. Reused by the favorite selector and
 * the home "Ajouter" CTA.
 */
export default function ServiceLogo({
  service,
  size = 20,
}: {
  service: StreamingService['id'] | string;
  size?: number;
}) {
  const id = normalizeServiceId(service);
  const png = PARTNER_PNG[id];
  if (png) {
    return (
      <img
        src={png}
        alt=""
        aria-hidden="true"
        style={{ width: size, height: size }}
        className="tw:object-contain"
      />
    );
  }
  if (id === 'soundcloud') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#FF5500" />
        <rect x="4" y="11" width="0.8" height="2" fill="white" />
        <rect x="5.2" y="10" width="0.8" height="4" fill="white" />
        <rect x="6.4" y="9" width="0.8" height="6" fill="white" />
        <rect x="7.6" y="10.5" width="0.8" height="3" fill="white" />
        <rect x="8.8" y="8" width="0.8" height="8" fill="white" />
        <rect x="10" y="9.5" width="0.8" height="5" fill="white" />
        <rect x="11.2" y="10" width="0.8" height="4" fill="white" />
        <path
          d="M18.5 10c-.8 0-1.5.3-2 .8-.3-1.4-1.5-2.3-3-2.3-1.2 0-2.2.7-2.7 1.7-.2-.1-.4-.2-.8-.2-1.1 0-2 .9-2 2s.9 2 2 2h8.5c1.4 0 2.5-1.1 2.5-2.5S19.9 10 18.5 10z"
          fill="white"
        />
      </svg>
    );
  }
  // youtube_music (default)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"
        fill="#FF0000"
      />
    </svg>
  );
}

/** Open-in-service search URL for a track. */
export function getServiceOpenUrl(service: string, query: string): string {
  const q = encodeURIComponent(query.trim());
  switch (normalizeServiceId(service)) {
    case 'spotify':
      return `https://open.spotify.com/search/${q}`;
    case 'deezer':
      return `https://www.deezer.com/search/${q}`;
    case 'apple_music':
      return `https://music.apple.com/search?term=${q}`;
    case 'soundcloud':
      return `https://soundcloud.com/search?q=${q}`;
    case 'youtube_music':
      return `https://music.youtube.com/search?q=${q}`;
    default:
      return `https://open.spotify.com/search/${q}`;
  }
}
